import { useState } from "react";
import { ChevronDown, BookOpen, Sparkles, Clock, Tag, Hash } from "lucide-react";
import SectionHeading, { SectionDivider } from "@/components/SectionHeading";

// SEO blog — targets the keywords: عمل حر، مستقل، فريلانسر، حاسبة مطورين، حاسبة عمل حر
// Articles live inside the single-page site so Googlebot can index semantic content
// around the calculator (improves topical authority + long-tail rankings).
// Each article carries a `hashtags` array that renders as clickable Google-search
// pills at the bottom of the article AND feeds the JSON-LD `keywords` property.
const ARTICLES_AR = [
  {
    id: "blog-prolancer-market-observer",
    tag: "مؤشرات أسعار",
    minutes: 5,
    title: "مؤشرات أسعار العمل الحر في السعودية — مرصد برولانسر للدراسات والتحليل",
    excerpt:
      "مؤشرات سعرية شفافة لأهم 8 خدمات في العمل الحر السعودي، مجمّعة من بيانات برولانسر خلال آخر 30 يومًا — أداة مجانية للباحثين والمستقلين والشركات لفهم اتجاهات التسعير.",
    hashtags: ["#مؤشرات_أسعار", "#مرصد_السوق", "#برولانسر", "#العمل_الحر_السعودية", "#بيانات_السوق", "#دراسات_التسعير"],
    sections: [
      {
        h: "ما هو مرصد مؤشرات برولانسر؟",
        p: "مرصد مجاني يعرض متوسط السعر المرصود لثمان خدمات رئيسية في العمل الحر السعودي (تطوير المواقع والتطبيقات، التصميم الجرافيكي، UI/UX، كتابة المحتوى، التسويق الرقمي، مونتاج الفيديو، استشارات SEO) مع عدد العيّنات واتجاه التغيّر خلال آخر 30 يومًا. البيانات تُحدَّث كل 5 دقائق ومتاحة عبر صفحة prolancer.app/market بالإضافة إلى API مفتوح.",
      },
      {
        h: "لمن هذا المرصد؟",
        p: "للباحثين الأكاديميين الذين يحتاجون أرقامًا قابلة للاستشهاد في بحوث العمل الحر، للمستقلين الذين يريدون مقارنة سعرهم باتجاه السوق، لفرق الموارد البشرية في الشركات لحساب ميزانيات التعاقدات الحرة، وللصحفيين والمدونين الذين يكتبون عن الاقتصاد الرقمي السعودي.",
      },
      {
        h: "منهجية البيانات",
        p: "المؤشرات مبنية على استعلامات التسعير الحقيقية التي يُجريها المستقلون على منصة برولانسر خلال آخر 30 يومًا، بمقارنة بآخر 30 يومًا سابقة لحساب نسبة التغيّر. تُعرض كل بطاقة خدمة بوضوح عدد العيّنات، ويظهر وسم \"عينة محدودة\" إذا كان العدد أقل من 5، وتُستبدل القيمة بـ \"لا توجد بيانات كافية\" إذا كان عدد العيّنات صفرًا.",
      },
      {
        h: "كيف أستشهد بالبيانات؟",
        p: "البيانات متاحة تحت ترخيص CC BY 4.0 — يكفي الإشارة للمصدر: Prolancer Market Observer مع رابط https://prolancer.app/market وذكر تاريخ آخر تحديث. لا تمثّل هذه البيانات إحصاءً رسميًا أو شاملًا للسوق، وتُستخدم للتحليل والدراسات ولا تُعتبر تسعيرة إلزامية.",
      },
    ],
  },
  {
    id: "blog-market-use-cases",
    tag: "تطبيقات المرصد",
    minutes: 6,
    title: "٥ طرق عملية لاستخدام مؤشرات أسعار العمل الحر في قراراتك اليومية",
    excerpt:
      "من المستقل الذي يتفاوض على سعر، إلى الباحث الأكاديمي، إلى مدير التوظيف — دليل مبسّط لتحويل مؤشرات برولانسر إلى قرارات أذكى.",
    hashtags: ["#مرصد_السوق", "#مؤشرات_أسعار", "#برولانسر", "#تسعير_الفريلانسر", "#بيانات_السوق"],
    sections: [
      {
        h: "1. المستقل: قارن سعرك باتجاه السوق قبل التفاوض",
        p: "قبل أن ترسل عرض سعر للعميل، افتح صفحة المرصد وشاهد متوسط السعر المرصود لخدمتك. إذا كان سعرك أقل بـ ٢٠٪ من المتوسط فأنت تبخس حقك، وإذا كان أعلى بـ ٥٠٪ فأنت على الأرجح تحتاج تبرير قيمة إضافية (شهادات، أمثلة، سرعة تسليم). الهدف أن تدخل الصفقة بسعر مدعوم بالأرقام لا بحدسك.",
      },
      {
        h: "2. العميل والشركة: ضع ميزانية واقعية للتعاقد الحر",
        p: "فرق الموارد البشرية ومديرو المشاريع يحتاجون أرقامًا للموازنة. استخدم \"متوسط السعر المرصود\" كنقطة بداية، ثم أضف ١٠-٢٠٪ هامش جودة إذا كنت تبحث عن مستقل خبير. تجنب أن تضع ميزانية بدون مرجع — غالبًا ما تجذب هذه الوظائف مستقلين مبتدئين فقط.",
      },
      {
        h: "3. الباحث الأكاديمي: استشهد بالبيانات في ورقتك",
        p: "البيانات متاحة تحت ترخيص CC BY 4.0 لأي بحث أو ورقة بشرط ذكر المصدر. أضف في المراجع: \"Prolancer Market Observer, https://prolancer.app/market, آخر تحديث: [التاريخ]\". المرصد يعطيك أيضًا API مفتوح لجلب الأرقام برمجيًا إذا كنت تبني تحليلات مكرّرة.",
      },
      {
        h: "4. الصحفي والمدوّن: استخرج قصّة من اتجاه الأرقام",
        p: "اتجاه التغيّر (+/-%) خلال ٣٠ يومًا هو مادة قصّة بذاته. مثلًا: \"ارتفعت أسعار كتابة المحتوى في السوق السعودي ١٢٪ هذا الشهر وفقًا لمؤشرات برولانسر\". اربط ذلك بسياق أوسع — موسم، فعاليات، تغيّرات تنظيمية — وستحصل على مقال فريد مدعوم بالبيانات.",
      },
      {
        h: "5. فرق المنتج: تحقّق من اتجاهات الطلب",
        p: "القسم \"الأعلى طلبًا · ٧ أيام\" يكشف أي خدمة يتدفق إليها الطلب الآن. إذا كنت تدير منصة أو تبني أداة للمستقلين، هذه الإشارة تساعدك على ترتيب أولويات المنتج وفقًا للاتجاه الحقيقي للسوق.",
      },
    ],
  },
  {
    id: "blog-haseba-amal-hor",
    tag: "حاسبة عمل حر",
    minutes: 4,
    title: "حاسبة عمل حر بالريال السعودي 2026: كيف تسعّر خدماتك باحتراف؟",
    excerpt:
      "دليل عملي للعاملين في العمل الحر داخل السعودية لتسعير أي خدمة بالريال، مع صيغة واضحة تجمع بين ساعات العمل، الخبرة، والتعقيد وأسعار السوق.",
    hashtags: ["#تسعير_الفريلانسر", "#حاسبة_تسعير", "#العمل_الحر_السعودية", "#برولانسر", "#فريلانسر_السعودية"],
    sections: [
      {
        h: "لماذا تحتاج حاسبة عمل حر؟",
        p: "التسعير العشوائي هو السبب رقم واحد لفقدان العملاء أو العمل بأقل من المطلوب. حاسبة برولانسر للعمل الحر تحوّل الحدس إلى رقم مبنيّ على بيانات السوق السعودية الحالية، وتعطيك ثلاث خيارات: سعر الفوز، السعر الموصى به، وسعر القيمة العالية.",
      },
      {
        h: "عناصر المعادلة",
        p: "نجمع بين: ساعات التنفيذ المتوقعة × سعر الساعة بالسوق × معامل الخبرة (مبتدئ، متوسط، خبير) × معامل التعقيد (بسيط، متوسط، معقد) × عدد التعديلات + رسوم الاستعجال. والنتيجة سعر عادل للطرفين.",
      },
      {
        h: "مثال سريع",
        p: "مطور ويب خبير يصمم متجر إلكتروني متوسط التعقيد لمدة 40 ساعة سيحصل غالبًا على سعر بين 7,000 و 12,000 ريال وفق متوسط السوق السعودي لعام 2026 — واستخدام الحاسبة يوفر لك دقائق من المقارنات ويضعك في النطاق الذي يفوز به المستقلون.",
      },
    ],
  },
  {
    id: "blog-haseba-motawerin",
    tag: "حاسبة مطورين",
    minutes: 5,
    title: "حاسبة مطورين سعودية: أسعار الويب، التطبيقات والـ UI/UX",
    excerpt:
      "متوسطات أسعار المطورين في السوق السعودي حسب التخصص والخبرة، مع نصائح لرفع سعر الساعة دون خسارة العملاء.",
    hashtags: ["#مطور_مواقع", "#مطور_تطبيقات", "#تصميم_UIUX", "#تسعير_الفريلانسر", "#فريلانسر_السعودية", "#برولانسر"],
    sections: [
      {
        h: "تخصصات تدخل في حاسبة المطورين",
        p: "تطوير مواقع (Front/Back-end)، تطوير تطبيقات iOS و Android، تصميم UI/UX، تكامل APIs، استشارات SEO تقنية، وتصميم جرافيكي. برولانسر تدعم 8 تخصصات رئيسية بنطاقات سعرية محدّثة.",
      },
      {
        h: "أسعار الساعة التقديرية 2026",
        p: "تطوير ويب خبير: 150–350 ريال/ساعة. تطوير تطبيقات: 180–400 ريال/ساعة. UI/UX خبير: 120–280 ريال/ساعة. استشارات SEO: 100–250 ريال/ساعة. (تختلف حسب حجم العميل والتعقيد.)",
      },
      {
        h: "كيف ترفع سعرك كمطوّر؟",
        p: "ركّز على نتائج قابلة للقياس (تحسين السرعة، زيادة المبيعات)، اعرض شهادات من عملاء سعوديين، وسجّل خدماتك على برولانسر للوصول إلى عملاء يبحثون تحديدًا عن مطورين موثوقين في السوق السعودي.",
      },
    ],
  },
  {
    id: "blog-freelancer-saudi",
    tag: "فريلانسر",
    minutes: 4,
    title: "فريلانسر في السعودية 2026: الوثائق، الضريبة، والمنصات الموثوقة",
    excerpt:
      "دليل شامل للفريلانسر السعودي أو المقيم: من استخراج رخصة العمل الحر إلى اختيار المنصة المناسبة لعرض خدماتك.",
    hashtags: ["#رخصة_العمل_الحر", "#فريلانسر_السعودية", "#العمل_الحر_السعودية", "#برولانسر", "#رؤية_2030"],
    sections: [
      {
        h: "رخصة العمل الحر",
        p: "منصة العمل الحر الحكومية تمنح رخصة مجانية للسعوديين والمقيمين، تؤهلك للتعاقد مع الشركات والجهات الحكومية رسميًا وفتح حساب بنكي تجاري.",
      },
      {
        h: "الضريبة والعمولة",
        p: "ضريبة القيمة المضافة 15% تُطبّق إذا تجاوز دخلك السنوي 375,000 ريال. العمولة على المنصات تتراوح بين 5% و20% — برولانسر تقدم عمولة تنافسية مع دعم باللغة العربية ودفع آمن داخل المملكة.",
      },
      {
        h: "كيف تختار منصة فريلانسر؟",
        p: "ابحث عن منصة محلية تدعم مدى والـ Apple Pay، لها آلية تحكيم عادلة بالعربية، وتسمح لك بعرض معرض أعمال احترافي. برولانسر مبنية خصيصًا لسوق المستقلين السعودي.",
      },
    ],
  },
  {
    id: "blog-mostaqel-tasier",
    tag: "مستقل",
    minutes: 3,
    title: "دليل المستقل المحترف: 7 أخطاء تسعير يجب تجنّبها",
    excerpt:
      "أكثر الأخطاء شيوعًا عند تسعير خدمات العمل الحر، وكيف تتفاداها لتحمي ربحيتك ووقتك.",
    hashtags: ["#تسعير_الفريلانسر", "#مستقل_سعودي", "#حاسبة_تسعير", "#أخطاء_التسعير", "#برولانسر"],
    sections: [
      {
        h: "الخطأ 1: تسعير بالساعة فقط",
        p: "العميل يرى الوقت ثمنًا لا قيمة. فعّل خيارات 'سعر المشروع' و'سعر القيمة' في حاسبة برولانسر لتعرض الفرق.",
      },
      {
        h: "الخطأ 2: نسيان التعديلات",
        p: "كل تعديل مجاني إضافي = ضياع 30 دقيقة من دخلك. حدّد عدد التعديلات (2 أو 3) داخل السعر، وأضف رسم للتعديلات الزائدة.",
      },
      {
        h: "الخطأ 3: الاستعجال بدون رسم إضافي",
        p: "إذا طلب العميل التسليم خلال 48 ساعة بدلًا من أسبوع، أضف 20–30% رسم استعجال — هذا مقبول عالميًا ومُدمج في حاسبة برولانسر.",
      },
      {
        h: "الأخطاء 4–7 باختصار",
        p: "تجاهل ضريبة القيمة المضافة، التسعير بأقل من السوق للمبتدئين، عدم رفع السعر مع الخبرة، وقبول مشاريع بدون عقد. الحاسبة تذكّرك بهذه النقاط تلقائيًا.",
      },
    ],
  },
  {
    id: "blog-prolancer-vs",
    tag: "برولانسر",
    minutes: 3,
    title: "لماذا برولانسر؟ منصة العمل الحر الأقرب للسوق السعودي",
    excerpt:
      "ما الذي يميّز برولانسر عن المنصات العالمية، وكيف تساعدك على البيع أسرع وبعائد أعلى.",
    hashtags: ["#برولانسر", "#منصة_برولانسر", "#فريلانسر_السعودية", "#مدى", "#ابل_باي", "#العمل_الحر_السعودية"],
    sections: [
      {
        h: "محلّية 100%",
        p: "أسعار بالريال، دعم بالعربية 24/7، دفع عبر مدى وفيزا والبنوك السعودية، وعقود متوافقة مع نظام العمل الحر في المملكة.",
      },
      {
        h: "عمولة منخفضة",
        p: "أقل عمولة في السوق السعودي مع ضمان حقوق المستقل، نظام تحكيم عادل، وإخلاء الدفعات أسرع من المنصات الأجنبية.",
      },
      {
        h: "ابدأ في 3 دقائق",
        p: "سجّل مجانًا على برولانسر، ارفع معرض أعمالك، واستخدم حاسبة برولانسر للعمل الحر لتسعير أول خدمة تنشرها. العملاء السعوديون في انتظارك.",
      },
    ],
  },

  /* -------- New 2026 SEO articles targeting high-intent Saudi freelance hashtags -------- */
  {
    id: "blog-tasier-fereelancer-dalil",
    tag: "تسعير الفريلانسر",
    minutes: 6,
    title: "تسعير الفريلانسر 2026: الدليل الشامل لحساب سعرك بثقة بالريال السعودي",
    excerpt:
      "خطوات عملية لحساب سعرك كفريلانسر سعودي: سعر الساعة المستهدف، العمولة، الضريبة، هامش الربح، وجدول أسعار جاهز لكل تخصص.",
    hashtags: [
      "#تسعير_الفريلانسر",
      "#حاسبة_تسعير",
      "#فريلانسر_السعودية",
      "#مستقل_سعودي",
      "#العمل_الحر_السعودية",
      "#رؤية_2030",
      "#زيادة_الدخل",
      "#برولانسر",
    ],
    sections: [
      {
        h: "الخطوة 1: احسب دخلك الشهري المستهدف",
        p: "ابدأ بالنهاية: كم تحتاج شهريًا لتعيش بالراحة التي تريدها؟ أضف 20% للطوارئ + 15% للضريبة + 10% للعمولة. المجموع يُقسم على عدد ساعات العمل الفعلية (ليست المتاحة) لتحصل على 'الحد الأدنى لسعر الساعة'.",
      },
      {
        h: "الخطوة 2: قارن بسعر السوق السعودي",
        p: "استخدم حاسبة برولانسر لمعرفة متوسط سعر الساعة في تخصصك — مطور ويب خبير اليوم بين 150–350 ر.س/ساعة، مصمم جرافيك خبير 90–220 ر.س/ساعة، كاتب محتوى احترافي 80–180 ر.س/ساعة. سعرك المستهدف يجب أن يقع في النصف الأعلى من هذا المدى بعد سنتين خبرة.",
      },
      {
        h: "الخطوة 3: أضف معاملات الواقع",
        p: "اضرب في معامل الخبرة (×0.7 مبتدئ، ×1 متوسط، ×1.4 خبير)، ثم في معامل التعقيد (×1، ×1.3، ×1.7)، وأخيرًا رسم الاستعجال 20–30%. الحاسبة تطبّق كل هذا تلقائيًا وتعرض لك ثلاث نطاقات: فوز سريع، موصى به، وقيمة عالية.",
      },
      {
        h: "الخطوة 4: اعرض السعر كاستثمار لا تكلفة",
        p: "في عرض السعر، اذكر النتيجة التي سيحصل عليها العميل (مثلًا: 'موقع يرفع تحويلات مبيعاتك 20%') قبل الرقم. المستقلون الذين يستخدمون هذه الطريقة يرفعون متوسط عرضهم بـ35% دون زيادة معدل الرفض.",
      },
      {
        h: "جدول أسعار بالريال السعودي 2026",
        p: "تصميم شعار: 500–3,500 ر.س. موقع تعريفي: 2,000–8,000 ر.س. متجر إلكتروني: 7,000–25,000 ر.س. تطبيق موبايل أساسي: 15,000–60,000 ر.س. مقال احترافي 1,000 كلمة: 150–600 ر.س. إدارة سوشال ميديا شهرية: 1,500–7,000 ر.س. (مصدر: متوسط عينة من 2,000+ مشروع سعودي.)",
      },
    ],
  },
  {
    id: "blog-vat-freelancer",
    tag: "ضريبة القيمة المضافة",
    minutes: 5,
    title: "ضريبة القيمة المضافة 15% للفريلانسر السعودي: متى تطبّق وكيف تحتسبها؟",
    excerpt:
      "كل ما تحتاج معرفته عن ضريبة القيمة المضافة للمستقلين في السعودية — من حد التسجيل إلى طريقة إصدار الفاتورة الضريبية والعلاقة مع ZATCA.",
    hashtags: [
      "#ضريبة_القيمة_المضافة",
      "#زاتكا",
      "#ZATCA",
      "#فريلانسر_السعودية",
      "#العمل_الحر_السعودية",
      "#فاتورة_ضريبية",
      "#مستقل_سعودي",
      "#برولانسر",
    ],
    sections: [
      {
        h: "ما هو حد التسجيل الإلزامي؟",
        p: "إذا تجاوز إجمالي إيراداتك السنوية 375,000 ريال، التسجيل في ضريبة القيمة المضافة لدى هيئة الزكاة والضريبة والجمارك (ZATCA) إلزامي. التسجيل الاختياري متاح إذا تجاوز دخلك 187,500 ريال وقد يفيدك إن كنت تبيع لشركات مسجّلة.",
      },
      {
        h: "كيف تُطبَّق الضريبة على خدماتك؟",
        p: "تُضاف 15% على قيمة الفاتورة. إذا سعّرت خدمة بـ10,000 ريال، تصبح الفاتورة النهائية 11,500 ريال. تحتسب هذه الـ1,500 ريال لحساب ZATCA عند التقديم الفصلي أو الشهري. حاسبة الدخل في برولانسر تفعّل مفتاح VAT تلقائيًا وتفصل الصافي عن الإجمالي.",
      },
      {
        h: "متطلبات الفاتورة الضريبية الإلكترونية (الفوترة الإلكترونية)",
        p: "منذ المرحلة الثانية من 'فاتورة'، أصبح على المسجّلين إصدار فواتير إلكترونية متوافقة مع ZATCA (XML + QR + ربط API). يمكنك استخدام منصات محلية معتمدة أو برولانسر Business الذي يُولّد فواتير متوافقة مباشرة من لوحة المستقل.",
      },
      {
        h: "ما الفرق إن كان العميل داخل/خارج السعودية؟",
        p: "عميل داخل المملكة: ضريبة 15%. عميل في دول الخليج المسجّلة: ضريبة 15% حتى اكتمال الربط الخليجي. عميل خارج دول الخليج: 'صفر ضريبة' (zero-rated) — تحسبها 0% لكن تذكرها في الإقرار. حاسبة برولانسر تنبّهك إلى هذه الاستثناءات.",
      },
      {
        h: "نصيحة ذهبية لتجنّب الصدمة",
        p: "افتح حسابًا بنكيًا منفصلًا لإيداع 15% من كل دفعة فور وصولها. بهذه الطريقة لن تتفاجأ بالإقرار الفصلي أبدًا، وستحتفظ بسمعتك مع ZATCA — وهو ما يفتح لك باب العقود الحكومية والمشاريع الكبيرة لاحقًا.",
      },
    ],
  },
  {
    id: "blog-tasier-tatbiqat-ios",
    tag: "تسعير التطبيقات",
    minutes: 5,
    title: "كم يكلّف تطوير تطبيق iOS في السعودية 2026؟ تحليل كامل بالأسعار",
    excerpt:
      "تحليل عملي لأسعار تطوير تطبيقات iOS و Android في السوق السعودي، مع تقسيم تكلفة كل مرحلة (تصميم، تطوير، باك-إند، اختبار، نشر).",
    hashtags: [
      "#مطور_تطبيقات",
      "#تطوير_تطبيقات_السعودية",
      "#iOS_السعودية",
      "#أندرويد",
      "#تسعير_الفريلانسر",
      "#حاسبة_تسعير",
      "#برولانسر",
      "#فريلانسر_السعودية",
    ],
    sections: [
      {
        h: "تطبيق بسيط (MVP) — ما السعر الواقعي؟",
        p: "تطبيق MVP بشاشات 5–8، تسجيل دخول، وبنية بسيطة يتراوح بين 15,000 و 40,000 ريال في السوق السعودي 2026. يتضمّن: UI/UX (3,000–8,000 ر.س) + تطوير iOS أو Android (10,000–25,000 ر.س) + Firebase/باك-إند خفيف (2,000–7,000 ر.س).",
      },
      {
        h: "تطبيق متوسط التعقيد (E-commerce / Social)",
        p: "تطبيق فيه نظام دفع، إشعارات، دردشة، ولوحة أدمن ينطلق من 60,000 ر.س ويصل إلى 180,000 ر.س. السبب: تكامل بوابة الدفع (Tap/HyperPay/مدى)، نظام مراجعات، فلاتر بحث، ولوحة تحكم كاملة.",
      },
      {
        h: "تطبيق على منصّتين (iOS + Android)",
        p: "الخياران الذكيان: 1) Flutter/React Native (أرخص بـ30–40% من الكود الأصلي، مناسب لـ 90% من التطبيقات). 2) Native (أعلى أداءً وتجربة، لازم لتطبيقات الكاميرا/الـAR). الحاسبة في برولانسر تسألك عن التقنية وتعدّل التقدير تلقائيًا.",
      },
      {
        h: "ما لا يراه العميل من التكلفة",
        p: "حساب Apple Developer: 99$ سنويًا. Google Play: 25$ مرة واحدة. خوادم باك-إند: 300–2,000 ر.س شهريًا. شهادات SSL + صيانة + تحديثات iOS السنوية = 15–20% من قيمة المشروع الأصلي كل سنة. اذكرها بوضوح في عرض السعر.",
      },
      {
        h: "كم تطلب كمطوّر؟",
        p: "خبير سعودي: 180–400 ريال/ساعة. فريق صغير (3 أشخاص): سعر مشروع بين 80,000 و 300,000 ر.س حسب نطاق المشروع. استخدم حاسبة برولانسر لتحديد السعر الموصى به بدلًا من التخمين، وستفوز بأكثر من 60% من العروض وفق إحصائيات 2026.",
      },
    ],
  },
  {
    id: "blog-tasweeq-raqami",
    tag: "تسويق رقمي",
    minutes: 4,
    title: "دليل أسعار التسويق الرقمي في السعودية 2026 بالريال",
    excerpt:
      "متوسطات أسعار إدارة الحملات، السوشال ميديا، الـSEO، والإعلانات في السوق السعودي — مع نصائح لتسعير باقاتك وزيادة قيمة العميل الواحد.",
    hashtags: [
      "#تسويق_رقمي",
      "#تسويق_رقمي_السعودية",
      "#SEO_السعودية",
      "#سوشال_ميديا_السعودية",
      "#حملات_اعلانية",
      "#تسعير_الفريلانسر",
      "#مستقل_سعودي",
      "#برولانسر",
    ],
    sections: [
      {
        h: "إدارة سوشال ميديا شهرية",
        p: "باقة أساسية (12 منشور + ردود + تقرير شهري): 1,500–3,500 ر.س. باقة احترافية (24 منشور + ريلز + حملات صغيرة): 3,500–7,000 ر.س. باقة كاملة (قناة فيديو + ستوري يومي + إدارة إعلانات): 7,000–18,000 ر.س/شهر.",
      },
      {
        h: "إعلانات ممولة (Meta / Google / TikTok)",
        p: "رسوم إدارة الحملة: 15–25% من ميزانية الإنفاق الإعلاني شهريًا، بحد أدنى 1,500 ر.س. مثلًا: عميل ينفق 20,000 ر.س إعلانات، رسوم الإدارة 3,000–5,000 ر.س. برولانسر توفر لك قوالب جاهزة لعرض الباقات بشفافية.",
      },
      {
        h: "تحسين محركات البحث (SEO)",
        p: "تدقيق تقني لموقع متوسط: 2,500–6,000 ر.س. استراتيجية كلمات مفتاحية + On-page: 3,000–9,000 ر.س. اشتراك شهري (مقالات + روابط + تقارير): 2,500–8,500 ر.س/شهر. أسعار سوق السعودية ترتفع 15% سنويًا مع نمو الطلب.",
      },
      {
        h: "كيف ترفع قيمة العميل الواحد؟",
        p: "اعرض 'باقة سنوية بخصم 10%' + تقرير ربع سنوي + مكالمة استراتيجية شهرية. هذا يرفع متوسط دخلك لكل عميل من 3,000 إلى 7,500 ر.س/شهر ويقلل عدد العملاء الذي تحتاج إدارتهم. الحاسبة تساعدك في بناء باقات ثلاثية (أساسية/موصى بها/مميزة).",
      },
    ],
  },
  {
    id: "blog-rafaa-dakhl",
    tag: "زيادة الدخل",
    minutes: 5,
    title: "من 3,000 إلى 25,000 ريال/شهر: استراتيجية 5 خطوات للفريلانسر السعودي",
    excerpt:
      "خارطة طريق واقعية لمضاعفة دخلك كفريلانسر خلال 12 شهر، مبنية على قصص نجاح مستقلين سعوديين استخدموا حاسبة برولانسر.",
    hashtags: [
      "#زيادة_الدخل",
      "#فريلانسر_السعودية",
      "#مستقل_سعودي",
      "#دخل_شهري",
      "#تسعير_الفريلانسر",
      "#نجاح_مالي",
      "#برولانسر",
      "#رؤية_2030",
    ],
    sections: [
      {
        h: "الخطوة 1: أوقف خسائر التسعير أولًا (الشهر 1)",
        p: "قبل أن تفكّر في زيادة العملاء، أوقف النزيف. ادخل حاسبة برولانسر، احسب 'سعر الساعة العادل' وقارنه بالسعر الحالي. متوسط الفارق لدى من يستخدمون الحاسبة أول مرّة: 40% تحت السوق. ارفع أسعار العملاء الجدد فورًا للنطاق الموصى به.",
      },
      {
        h: "الخطوة 2: تخلّص من 20% من العملاء المرهقين (الشهر 2)",
        p: "قاعدة 80/20: 20% من عملائك يستهلكون 80% من وقتك براتب ضعيف. أرسل لهم إشعار رفع سعر بـ30% — من يتقبّل تابع معه، ومن يرفض ستوفّر وقتك لعملاء أعلى قيمة.",
      },
      {
        h: "الخطوة 3: ابنِ 'عرض باقات' بدل الساعة (الأشهر 3–4)",
        p: "حوّل خدماتك إلى 3 باقات ثابتة (أساسي / موصى به / مميّز). عملاء السعودية يفضّلون وضوح الأسعار. متوسط الزيادة في متوسط قيمة العميل: 2.5×. برولانسر تولّد لك عرض باقات جاهزًا من ضغطة زر.",
      },
      {
        h: "الخطوة 4: استثمر في تخصّص واحد عالي الطلب (الأشهر 5–8)",
        p: "التخصصات الأعلى طلبًا في السعودية 2026: تطوير متاجر Shopify/Salla، تطبيقات Flutter، تصميم UI/UX للتطبيقات الحكومية، تسويق Reels/TikTok، كتابة محتوى تقني بالعربية. اختر واحدة واكسب لقب 'الأفضل في هذا التخصّص'.",
      },
      {
        h: "الخطوة 5: ضاعف السعر مرة أخرى + اشتراك شهري (الأشهر 9–12)",
        p: "بعد سنة من الالتزام: متوسط سعر الساعة لمن اتبعوا هذه الاستراتيجية ارتفع من 50 ر.س إلى 220 ر.س. أضف نموذج 'retainer' (اشتراك شهري ثابت 3,000–8,000 ر.س) مع 2–3 عملاء، وستكسر حاجز 25,000 ر.س شهريًا بأمان.",
      },
    ],
  },
];

const ARTICLES_EN = [
  {
    id: "blog-prolancer-market-observer",
    tag: "Market Indicators",
    minutes: 5,
    title: "Saudi Freelance Price Indicators — Prolancer Market Observer for Research & Analysis",
    excerpt:
      "Transparent price indicators for the top 8 services in Saudi freelancing, aggregated from Prolancer data over the last 30 days — a free tool for researchers, freelancers, and companies to understand pricing trends.",
    hashtags: ["#MarketIndicators", "#MarketObserver", "#Prolancer", "#SaudiFreelance", "#MarketData", "#PricingResearch"],
    sections: [
      {
        h: "What is the Prolancer Market Observer?",
        p: "A free observatory showing the observed average price for 8 core Saudi freelance services (web & app development, graphic design, UI/UX, content writing, digital marketing, video editing, SEO consulting), along with sample counts and 30-day trend direction. Data refreshes every 5 minutes and is available at prolancer.app/market plus an open API.",
      },
      {
        h: "Who is it for?",
        p: "Academic researchers who need citable numbers in freelancing studies, freelancers benchmarking their rates against market trends, HR teams at companies budgeting for freelance engagements, and journalists & bloggers covering the Saudi digital economy.",
      },
      {
        h: "Data methodology",
        p: "Indicators are built from real pricing queries run by freelancers on the Prolancer platform over the last 30 days, compared against the previous 30 days to compute trend %. Each service card clearly shows sample count, flags \"limited sample\" when fewer than 5, and replaces the value with \"Not enough data yet\" if the sample count is zero.",
      },
      {
        h: "How to cite the data",
        p: "The data is available under CC BY 4.0 — just attribute the source: Prolancer Market Observer with the link https://prolancer.app/market and mention the last-updated date. This data does not represent an official or exhaustive market statistic and is intended for analysis and research, not as a binding rate.",
      },
    ],
  },
  {
    id: "blog-market-use-cases",
    tag: "Observatory Use Cases",
    minutes: 6,
    title: "5 Practical Ways to Use Saudi Freelance Price Indicators in Your Daily Decisions",
    excerpt:
      "From the freelancer negotiating a rate, to the academic researcher, to the hiring manager — a simple guide to turning Prolancer's indicators into smarter decisions.",
    hashtags: ["#MarketObserver", "#MarketIndicators", "#Prolancer", "#FreelancerPricing", "#MarketData"],
    sections: [
      {
        h: "1. Freelancer — benchmark your rate before negotiating",
        p: "Before you send a quote to a client, open the observatory and look up the observed average for your service. If your rate is 20% below the average you may be underselling; if it's 50% above, you likely need to justify extra value (certifications, portfolio, delivery speed). The goal is to enter the deal with a data-backed number, not a gut feeling.",
      },
      {
        h: "2. Clients & companies — set a realistic freelance budget",
        p: "HR teams and project managers need numbers for budgeting. Use the \"observed average price\" as a starting point, then add a 10-20% quality margin if you want an expert. Avoid setting a budget without any reference — those listings typically only attract beginners.",
      },
      {
        h: "3. Academic researchers — cite the data in your paper",
        p: "The data is available under CC BY 4.0 for any paper or study, as long as the source is cited. Add to your references: \"Prolancer Market Observer, https://prolancer.app/market, last updated: [date]\". The observatory also exposes an open API for programmatic access when building recurring analyses.",
      },
      {
        h: "4. Journalists & bloggers — extract a story from the trends",
        p: "The 30-day trend direction (+/-%) is a story on its own. For example: \"Content writing prices in Saudi Arabia rose 12% this month according to Prolancer indicators\". Pair that with broader context — season, events, regulatory changes — and you have a unique data-backed article.",
      },
      {
        h: "5. Product teams — validate demand direction",
        p: "The \"Hottest · 7d\" section reveals which service is seeing a surge right now. If you run a platform or build a freelancer tool, this signal helps you prioritize features along the real direction of the market.",
      },
    ],
  },
  {
    id: "blog-haseba-amal-hor",
    tag: "Freelance Calculator",
    minutes: 4,
    title: "Freelance Calculator in SAR: How to Price Your Services Like a Pro (2026)",
    excerpt:
      "Practical guide for Saudi freelancers to price any service in SAR with a transparent formula based on hours, experience, complexity, and market rates.",
    hashtags: ["#FreelancerPricing", "#PricingCalculator", "#SaudiFreelance", "#Prolancer"],
    sections: [
      {
        h: "Why you need a freelance calculator",
        p: "Random pricing is the #1 reason freelancers lose clients or undercharge. The Prolancer Freelance Calculator converts gut-feel into a data-backed number and gives you three tiers: win-rate price, recommended price, and premium price.",
      },
      {
        h: "The formula behind it",
        p: "We combine: hours × market hourly rate × experience multiplier × complexity multiplier × revisions + rush fee. The result is a fair range for both sides.",
      },
      {
        h: "Quick example",
        p: "An expert web developer building a medium-complexity e-commerce site for 40 hours should land between 7,000–12,000 SAR per Saudi 2026 market averages.",
      },
    ],
  },
  {
    id: "blog-haseba-motawerin",
    tag: "Developer Rates",
    minutes: 5,
    title: "Saudi Developer Rates 2026: Web, Mobile, and UI/UX",
    excerpt:
      "Market averages for Saudi developers by specialty and experience, with tips to raise your hourly rate without losing clients.",
    hashtags: ["#WebDeveloper", "#MobileDeveloper", "#UIUXDesign", "#SaudiFreelance", "#Prolancer"],
    sections: [
      {
        h: "Specialties covered",
        p: "Front-end & back-end web development, iOS & Android apps, UI/UX design, API integration, technical SEO, and graphic design. Prolancer supports 8 core specialties with up-to-date rate bands.",
      },
      {
        h: "Hourly estimates (2026)",
        p: "Expert web dev: 150–350 SAR/h. Mobile dev: 180–400 SAR/h. Expert UI/UX: 120–280 SAR/h. SEO consulting: 100–250 SAR/h.",
      },
      {
        h: "How to raise your rate",
        p: "Lead with measurable outcomes, show testimonials from Saudi clients, and list your services on Prolancer to reach buyers searching specifically for trusted Saudi developers.",
      },
    ],
  },
  {
    id: "blog-freelancer-saudi",
    tag: "Freelancer",
    minutes: 4,
    title: "Freelancing in Saudi Arabia 2026: License, Tax, and Trusted Platforms",
    excerpt:
      "A complete guide for Saudi and resident freelancers — from the official freelance license to picking the right platform.",
    hashtags: ["#SaudiFreelance", "#FreelanceLicense", "#Vision2030", "#Prolancer"],
    sections: [
      {
        h: "Freelance license",
        p: "The official Saudi freelance portal issues a free license for both citizens and residents, letting you contract with companies and government entities and open a commercial bank account.",
      },
      {
        h: "Tax and commission",
        p: "15% VAT applies if your annual income exceeds 375,000 SAR. Platform commissions range from 5% to 20% — Prolancer offers a competitive rate with Arabic support and in-Kingdom secure payments.",
      },
      {
        h: "How to pick a platform",
        p: "Look for a local platform that supports Mada and Apple Pay, has a fair Arabic-language arbitration process, and lets you showcase a professional portfolio. Prolancer is built for the Saudi freelance market.",
      },
    ],
  },
  {
    id: "blog-mostaqel-tasier",
    tag: "Pro Tips",
    minutes: 3,
    title: "The Independent Pro's Guide: 7 Pricing Mistakes to Avoid",
    excerpt:
      "The most common freelance pricing mistakes and how to avoid them to protect your profit and time.",
    hashtags: ["#FreelancerPricing", "#PricingMistakes", "#PricingCalculator", "#Prolancer"],
    sections: [
      {
        h: "Mistake 1: hourly-only pricing",
        p: "Clients see time as cost, not value. Switch to project or value-based pricing using Prolancer's three-tier output.",
      },
      {
        h: "Mistake 2: ignoring revisions",
        p: "Each free revision = 30 minutes gone. Cap revisions (2 or 3) in-scope and charge for extras.",
      },
      {
        h: "Mistake 3: rush with no premium",
        p: "If the client wants a 48-hour turnaround instead of a week, add 20–30% rush — standard worldwide and built into the Prolancer calculator.",
      },
      {
        h: "Mistakes 4–7 in brief",
        p: "Ignoring VAT, underpricing as a beginner, not raising rates with experience, and accepting projects without contracts. The calculator nudges you on each.",
      },
    ],
  },
  {
    id: "blog-prolancer-vs",
    tag: "Prolancer",
    minutes: 3,
    title: "Why Prolancer? The Freelance Platform Built for Saudi Arabia",
    excerpt:
      "What makes Prolancer different from global marketplaces, and how it helps you sell faster at a higher rate.",
    hashtags: ["#Prolancer", "#SaudiFreelance", "#Mada", "#ApplePay"],
    sections: [
      {
        h: "100% local",
        p: "Prices in SAR, 24/7 Arabic support, Mada/Visa/local bank payouts, and contracts aligned with the Saudi freelance regulations.",
      },
      {
        h: "Lower commission",
        p: "The lowest commission on the Saudi market with freelancer protections, a fair arbitration system, and faster payouts than foreign platforms.",
      },
      {
        h: "Start in 3 minutes",
        p: "Sign up free on Prolancer, upload your portfolio, and use the Prolancer Freelance Calculator to price your first service. Saudi clients are waiting.",
      },
    ],
  },
];

export default function Blog({ lang = "ar", theme = "dark" }) {
  const dark = theme === "dark";
  const ar = lang === "ar";
  const articles = ar ? ARTICLES_AR : ARTICLES_EN;
  const [openId, setOpenId] = useState(articles[0].id);

  return (
    <section
      id="blog"
      data-testid="blog-section"
      className={`no-print relative ${dark ? "bg-[#182138] text-white" : "bg-white text-[#2d2c35]"}`}
      dir={ar ? "rtl" : "ltr"}
    >
      <SectionDivider theme={theme} />
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-20">
        <SectionHeading
          lang={lang}
          theme={theme}
          icon={<BookOpen className="h-3.5 w-3.5" />}
          eyebrow={ar ? "مدوّنة برولانسر" : "Prolancer Blog"}
          title={
            ar
              ? "دليلك الكامل لعالم العمل الحر والفريلانسر في السعودية"
              : "Your complete guide to freelancing in Saudi Arabia"
          }
          subtitle={
            ar
              ? "مقالات محدّثة لعام 2026 حول حاسبة المطورين، تسعير المستقلين، والعمل كفريلانسر في السوق السعودي — كل ما تحتاجه قبل إرسال أول عرض."
              : "2026-updated articles covering developer rates, freelance pricing, and working as a Saudi freelancer — everything you need before sending your first proposal."
          }
        />

        <div className="mt-10 space-y-3">
          {articles.map((a) => {
            const open = openId === a.id;
            return (
              <article
                key={a.id}
                id={a.id}
                data-testid={`blog-article-${a.id}`}
                className={`rounded-2xl border transition-all ${
                  open
                    ? dark
                      ? "bg-[#1f2a47] border-[#18c98f]/40 shadow-[0_10px_30px_-10px_rgba(24,201,143,0.25)]"
                      : "bg-[#f3f8ff] border-[#18c98f]/60 shadow-[0_10px_30px_-10px_rgba(24,201,143,0.25)]"
                    : dark
                    ? "bg-[#1f2a47] border-[#2e3a5c] hover:border-[#18c98f]/40"
                    : "bg-white border-[#e5e5ea] hover:border-[#18c98f]/60"
                }`}
              >
                <button
                  type="button"
                  onClick={() => setOpenId(open ? null : a.id)}
                  aria-expanded={open}
                  data-testid={`blog-toggle-${a.id}`}
                  className="w-full text-start px-5 sm:px-6 py-5 flex items-start gap-3 group"
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center flex-wrap gap-2 text-[11px] font-bold uppercase tracking-[0.2em] text-[#18c98f]">
                      <span className="inline-flex items-center gap-1">
                        <Tag className="h-3 w-3" /> {a.tag}
                      </span>
                      <span className={`inline-flex items-center gap-1 ${dark ? "text-white/50" : "text-[#2d2c35]/50"}`}>
                        <Clock className="h-3 w-3" /> {a.minutes} {ar ? "دقائق" : "min"}
                      </span>
                    </div>
                    <h3
                      className={`mt-2 text-lg sm:text-xl font-bold leading-snug ${ar ? "font-arabic" : "font-outfit"} ${dark ? "text-white" : "text-[#000000]"}`}
                    >
                      {a.title}
                    </h3>
                    <p className={`mt-1.5 text-sm leading-relaxed ${dark ? "text-white/70" : "text-[#2d2c35]/70"} ${ar ? "font-arabic" : "font-outfit"}`}>
                      {a.excerpt}
                    </p>
                  </div>
                  <ChevronDown
                    className={`h-5 w-5 mt-1 shrink-0 text-[#18c98f] transition-transform duration-300 ${
                      open ? "rotate-180" : ""
                    }`}
                  />
                </button>

                {open && (
                  <div className={`px-5 sm:px-6 pb-6 pt-1 border-t ${dark ? "border-[#2e3a5c]" : "border-[#e5e5ea]"}`}>
                    <div className="space-y-4 mt-4">
                      {a.sections.map((s, i) => (
                        <div key={i}>
                          <h4 className={`text-sm sm:text-base font-bold text-[#18c98f] ${ar ? "font-arabic" : "font-outfit"}`}>
                            {s.h}
                          </h4>
                          <p className={`mt-1.5 text-sm leading-relaxed ${dark ? "text-white/80" : "text-[#2d2c35]/85"} ${ar ? "font-arabic" : "font-outfit"}`}>
                            {s.p}
                          </p>
                        </div>
                      ))}
                    </div>

                    {/* Hashtag chips — SEO + social discoverability.
                        Clicking opens Google search for the hashtag so the
                        article contributes to local topical authority. */}
                    {Array.isArray(a.hashtags) && a.hashtags.length > 0 && (
                      <div
                        data-testid={`blog-hashtags-${a.id}`}
                        className="mt-5 flex flex-wrap gap-1.5"
                        dir="rtl"
                      >
                        {a.hashtags.map((h) => (
                          <a
                            key={h}
                            href={`https://www.google.com/search?q=${encodeURIComponent(h)}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            data-testid={`blog-hashtag-${a.id}-${h.replace(/[^\w]/g, "")}`}
                            className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[11px] font-bold border transition ${
                              dark
                                ? "bg-[#18c98f]/10 border-[#18c98f]/40 text-[#18c98f] hover:bg-[#18c98f]/25"
                                : "bg-[#18c98f]/10 border-[#18c98f]/40 text-[#12a777] hover:bg-[#18c98f]/25"
                            } font-arabic`}
                          >
                            <Hash className="h-3 w-3" />
                            <span>{h.replace(/^#/, "")}</span>
                          </a>
                        ))}
                      </div>
                    )}

                    <a
                      href="https://prolancer.sa"
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid={`blog-cta-${a.id}`}
                      className="mt-6 inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#18c98f] text-white text-xs font-extrabold hover:bg-[#18c98f] transition"
                    >
                      <Sparkles className="h-3.5 w-3.5" />
                      {ar ? "ابدأ الآن مع برولانسر" : "Start now with Prolancer"}
                    </a>
                  </div>
                )}
              </article>
            );
          })}
        </div>

        {/* Follow us + support links section removed per user request. */}

        {/* Hidden JSON-LD for each article so Google indexes them */}
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              "@context": "https://schema.org",
              "@type": "Blog",
              name: ar ? "مدوّنة برولانسر" : "Prolancer Blog",
              url: "https://prolancer.app/#blog",
              inLanguage: lang,
              blogPost: articles.map((a) => ({
                "@type": "BlogPosting",
                headline: a.title,
                description: a.excerpt,
                keywords: [a.tag, ...(a.hashtags || []).map((h) => h.replace(/^#/, "").replace(/_/g, " "))].join(", "),
                articleSection: a.tag,
                author: { "@type": "Organization", name: "Prolancer" },
                publisher: {
                  "@type": "Organization",
                  name: "Prolancer",
                  logo: {
                    "@type": "ImageObject",
                    url: "https://prolancer.app/prolancer-bird.png",
                  },
                },
                mainEntityOfPage: `https://prolancer.app/#${a.id}`,
              })),
            }),
          }}
        />

        {/* Organization + Social Profiles schema — links brand to social accounts */}
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              "@context": "https://schema.org",
              "@type": "Organization",
              name: "Prolancer — برولانسر",
              alternateName: ["برولانسر", "Prolancer Platform"],
              url: "https://prolancer.sa",
              logo: "https://prolancer.app/prolancer-bird.png",
              description: ar
                ? "منصة سعودية للعمل الحر تربط أصحاب المشاريع بأفضل المستقلين."
                : "Saudi freelance platform connecting project owners with top freelancers.",
              sameAs: [
                "https://www.instagram.com/prolancerplatform",
                "https://www.tiktok.com/@prolancer.platform",
                "https://wa.me/966532556796",
                "https://prolancer.sa",
                "https://prolancer.app",
              ],
              contactPoint: {
                "@type": "ContactPoint",
                telephone: "+966-53-255-6796",
                contactType: "customer support",
                email: "info@pro-lancer.net",
                availableLanguage: ["Arabic", "English"],
                areaServed: "SA",
              },
            }),
          }}
        />
      </div>
    </section>
  );
}
